import React, { useState, useEffect } from 'react';
import { X, Save, Sparkles, Lightbulb, Search, Loader } from 'lucide-react';
import apiService from '../services/api';
import composioService from '../services/composio';

// ── Composio node config ──────────────────────────────────────────────────────

function ComposioConfig({ config, onChange }) {
  const [connections, setConnections] = useState([]);
  const [tools, setTools] = useState([]);
  const [toolSearch, setToolSearch] = useState('');
  const [loadingTools, setLoadingTools] = useState(false);
  const selectedToolkit = config.toolkit || '';
  const selectedSlug = config.slug || '';

  useEffect(() => {
    composioService.getConnections().then(r => setConnections(r.connections || []));
  }, []);

  useEffect(() => {
    if (!selectedToolkit) { setTools([]); return; }
    setLoadingTools(true);
    composioService.getTools(selectedToolkit).then(t => {
      setTools(t);
      setLoadingTools(false);
    });
  }, [selectedToolkit]);

  const selectedTool = tools.find(t => t.id === selectedSlug);
  const props = selectedTool?.schema?.properties || {};
  const required = selectedTool?.schema?.required || [];
  const filteredTools = tools.filter(t =>
    !toolSearch || t.label.toLowerCase().includes(toolSearch.toLowerCase()) || t.id.toLowerCase().includes(toolSearch.toLowerCase())
  );

  function setArg(key, value) {
    onChange('args', { ...(config.args || {}), [key]: value });
  }

  return (
    <div className="space-y-3">
      {/* Toolkit picker */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">App / Toolkit</label>
        {connections.length === 0 ? (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            No apps connected yet — open Settings → Integrations to connect an app.
          </p>
        ) : (
          <select
            value={selectedToolkit}
            onChange={e => { onChange('toolkit', e.target.value); onChange('slug', ''); onChange('args', {}); }}
            className="w-full p-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">— pick an app —</option>
            {connections.map(c => (
              <option key={c.toolkit} value={c.toolkit}>{c.toolkit}</option>
            ))}
          </select>
        )}
      </div>

      {/* Tool picker */}
      {selectedToolkit && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Tool / Action</label>
          {loadingTools ? (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-1">
              <Loader size={12} className="animate-spin" /> Loading tools…
            </div>
          ) : (
            <>
              <div className="relative mb-1.5">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={toolSearch}
                  onChange={e => setToolSearch(e.target.value)}
                  placeholder="Search tools…"
                  className="w-full pl-6 pr-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={selectedSlug}
                onChange={e => { onChange('slug', e.target.value); onChange('args', {}); onChange('label', e.target.options[e.target.selectedIndex]?.text || ''); }}
                size={Math.min(5, filteredTools.length + 1)}
                className="w-full border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                style={{ minHeight: '80px' }}
              >
                <option value="">— pick a tool —</option>
                {filteredTools.map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              {tools.length > 0 && <p className="text-[10px] text-gray-400 mt-0.5">{tools.length} tools available</p>}
            </>
          )}
        </div>
      )}

      {/* Parameter inputs */}
      {selectedTool && Object.keys(props).length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Parameters</label>
          <div className="space-y-2">
            {Object.entries(props).map(([key, schema]) => {
              const isRequired = required.includes(key);
              return (
                <div key={key}>
                  <label className="block text-[10px] text-gray-500 mb-0.5">
                    {key}{isRequired && <span className="text-red-400 ml-0.5">*</span>}
                    {schema.description && <span className="text-gray-400 ml-1">— {schema.description.slice(0, 60)}</span>}
                  </label>
                  <input
                    type="text"
                    value={config.args?.[key] || ''}
                    onChange={e => setArg(key, e.target.value)}
                    placeholder={`{{variable}} or literal value`}
                    className="w-full p-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  />
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-gray-400 mt-1">Use {`{{varName}}`} to pass flow variables</p>
        </div>
      )}

      {/* Output variable */}
      {selectedSlug && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Store result in variable</label>
          <input
            type="text"
            value={config.outputVariable || ''}
            onChange={e => onChange('outputVariable', e.target.value)}
            placeholder="e.g. github_result"
            className="w-full p-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const NodeConfigModal = ({ node, onClose, onUpdate }) => {
  const [config, setConfig] = useState(node.data.config || {});
  const [label, setLabel] = useState(node.data.label || '');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeField, setActiveField] = useState(null);

  useEffect(() => {
    setConfig(node.data.config || {});
    setLabel(node.data.label || '');
    loadAISuggestions();
  }, [node]);

  const loadAISuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const suggestions = await apiService.getAISuggestions(node.type, config);
      setAiSuggestions(suggestions.suggestions || []);
    } catch (error) {
      console.error('Failed to load AI suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSave = () => {
    onUpdate(node.id, {
      label,
      config
    });
    onClose();
  };

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const applySuggestion = (suggestion, field) => {
    handleConfigChange(field, suggestion);
    setShowSuggestions(false);
  };

  const SuggestionButton = ({ suggestion, field }) => (
    <button
      onClick={() => applySuggestion(suggestion, field)}
      className="text-left p-2 text-sm bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors"
    >
      <div className="flex items-center gap-2">
        <Sparkles size={12} className="text-blue-500" />
        <span className="text-blue-700">{suggestion}</span>
      </div>
    </button>
  );

  const renderConfigFields = () => {
    switch (node.type) {
      case 'llm':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Model
              </label>
              <select
                value={config.model || 'gpt-3.5-turbo'}
                onChange={(e) => handleConfigChange('model', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                <option value="gpt-4">GPT-4</option>
                <option value="claude-3">Claude 3</option>
                <option value="llama-2">Llama 2</option>
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  System Prompt
                </label>
                <button
                  onClick={() => {
                    setActiveField('systemPrompt');
                    setShowSuggestions(!showSuggestions);
                  }}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <Lightbulb size={12} />
                  AI Suggestions
                </button>
              </div>
              <textarea
                value={config.systemPrompt || ''}
                onChange={(e) => handleConfigChange('systemPrompt', e.target.value)}
                placeholder="Enter system prompt..."
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24 resize-none"
              />
              {showSuggestions && activeField === 'systemPrompt' && (
                <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                  {loadingSuggestions ? (
                    <div className="text-sm text-gray-500 p-2">Loading suggestions...</div>
                  ) : (
                    aiSuggestions.map((suggestion, index) => (
                      <SuggestionButton
                        key={index}
                        suggestion={suggestion}
                        field="systemPrompt"
                      />
                    ))
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Temperature
              </label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={config.temperature || 0.7}
                onChange={(e) => handleConfigChange('temperature', parseFloat(e.target.value))}
                className="w-full"
              />
              <div className="text-sm text-gray-500 mt-1">
                Current: {config.temperature || 0.7}
              </div>
            </div>
          </>
        );
      
      case 'imageGen':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Model
              </label>
              <select
                value={config.model || 'dall-e-3'}
                onChange={(e) => handleConfigChange('model', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="dall-e-3">DALL-E 3</option>
                <option value="midjourney">Midjourney</option>
                <option value="stable-diffusion">Stable Diffusion</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image Size
              </label>
              <select
                value={config.size || '1024x1024'}
                onChange={(e) => handleConfigChange('size', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="512x512">512x512</option>
                <option value="1024x1024">1024x1024</option>
                <option value="1792x1024">1792x1024</option>
              </select>
            </div>
          </>
        );
      
      case 'dataInput':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Source
              </label>
              <select
                value={config.source || 'manual'}
                onChange={(e) => handleConfigChange('source', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="manual">Manual Input</option>
                <option value="file">File Upload</option>
                <option value="api">API Endpoint</option>
                <option value="database">Database</option>
              </select>
            </div>
            {config.source === 'manual' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data
                </label>
                <textarea
                  value={config.data || ''}
                  onChange={(e) => handleConfigChange('data', e.target.value)}
                  placeholder="Enter your data..."
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24 resize-none"
                />
              </div>
            )}
          </>
        );

      case 'message':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Text
              </label>
              <textarea
                value={config.message || ''}
                onChange={(e) => handleConfigChange('message', e.target.value)}
                placeholder="Enter message text... Use {{variable}} for dynamic content"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Type
              </label>
              <select
                value={config.type || 'text'}
                onChange={(e) => handleConfigChange('type', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="text">Text</option>
                <option value="markdown">Markdown</option>
                <option value="html">HTML</option>
              </select>
            </div>
          </>
        );

      case 'prompt':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prompt Text
              </label>
              <textarea
                value={config.prompt || ''}
                onChange={(e) => handleConfigChange('prompt', e.target.value)}
                placeholder="Enter prompt text... Use {{variable}} for dynamic content"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Variable Name (to store response)
              </label>
              <input
                type="text"
                value={config.variable || ''}
                onChange={(e) => handleConfigChange('variable', e.target.value)}
                placeholder="response"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Input Type
              </label>
              <select
                value={config.inputType || 'text'}
                onChange={(e) => handleConfigChange('inputType', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="email">Email</option>
                <option value="password">Password</option>
              </select>
            </div>
          </>
        );

      case 'button':
        return (
          <>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Button Options
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const currentButtons = config.buttons || [];
                    handleConfigChange('buttons', [...currentButtons, { text: '', value: '' }]);
                  }}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  + Add Button
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(config.buttons || [{ text: '', value: '' }]).map((button, index) => (
                  <div key={index} className="flex gap-2 items-center p-2 bg-gray-50 rounded border">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={button.text || ''}
                        onChange={(e) => {
                          const newButtons = [...(config.buttons || [])];
                          newButtons[index] = { ...newButtons[index], text: e.target.value, value: e.target.value };
                          handleConfigChange('buttons', newButtons);
                        }}
                        placeholder={`Button ${index + 1} text`}
                        className="w-full p-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newButtons = config.buttons?.filter((_, i) => i !== index) || [];
                        handleConfigChange('buttons', newButtons);
                      }}
                      className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Remove button"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              {(!config.buttons || config.buttons.length === 0) && (
                <div className="text-xs text-gray-500 text-center py-2">
                  Click "Add Button" to create button options
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Variable Name (to store selection)
              </label>
              <input
                type="text"
                value={config.variable || ''}
                onChange={(e) => handleConfigChange('variable', e.target.value)}
                placeholder="choice"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </>
        );

      case 'setVariable':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Variable Name
              </label>
              <input
                type="text"
                value={config.variableName || ''}
                onChange={(e) => handleConfigChange('variableName', e.target.value)}
                placeholder="myVariable"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Value
              </label>
              <textarea
                value={config.value || ''}
                onChange={(e) => handleConfigChange('value', e.target.value)}
                placeholder="Enter value... Use {{variable}} for dynamic content"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-20 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scope
              </label>
              <select
                value={config.scope || 'local'}
                onChange={(e) => handleConfigChange('scope', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="local">Local</option>
                <option value="global">Global</option>
              </select>
            </div>
          </>
        );

      case 'switch':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Variable to Switch On
              </label>
              <input
                type="text"
                value={config.variable || ''}
                onChange={(e) => handleConfigChange('variable', e.target.value)}
                placeholder="variableName"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cases (value:label, one per line)
              </label>
              <textarea
                value={config.cases?.map(c => `${c.value}:${c.label || c.value}`).join('\n') || ''}
                onChange={(e) => {
                  const lines = e.target.value.split('\n').filter(line => line.trim());
                  const cases = lines.map(line => {
                    const [value, label] = line.split(':');
                    return { value: value?.trim(), label: label?.trim() || value?.trim() };
                  });
                  handleConfigChange('cases', cases);
                }}
                placeholder="yes:Yes&#10;no:No&#10;maybe:Maybe"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24 resize-none"
              />
            </div>
          </>
        );

      case 'condition':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Condition Expression
              </label>
              <textarea
                value={config.condition || ''}
                onChange={(e) => handleConfigChange('condition', e.target.value)}
                placeholder="{{variable}} == 'value' or {{count}} > 5"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-20 resize-none"
              />
            </div>
            <div className="text-xs text-gray-500">
              Supported operators: ==, !=, &lt;, &gt;, &lt;=, &gt;=, and, or, not
            </div>
          </>
        );

      case 'apiCall':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                HTTP Method
              </label>
              <select
                value={config.method || 'GET'}
                onChange={(e) => handleConfigChange('method', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL
              </label>
              <input
                type="text"
                value={config.url || ''}
                onChange={(e) => handleConfigChange('url', e.target.value)}
                placeholder="https://api.example.com/endpoint"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Headers (JSON)
              </label>
              <textarea
                value={config.headers || ''}
                onChange={(e) => handleConfigChange('headers', e.target.value)}
                placeholder='{"Authorization": "Bearer {{token}}", "Content-Type": "application/json"}'
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-16 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Body (for POST/PUT)
              </label>
              <textarea
                value={config.body || ''}
                onChange={(e) => handleConfigChange('body', e.target.value)}
                placeholder='{"key": "{{value}}"}'
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-16 resize-none"
              />
            </div>
          </>
        );

      case 'function':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Function Name
              </label>
              <select
                value={config.functionName || 'sum'}
                onChange={(e) => handleConfigChange('functionName', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="sum">Sum</option>
                <option value="avg">Average</option>
                <option value="min">Minimum</option>
                <option value="max">Maximum</option>
                <option value="count">Count</option>
                <option value="concat">Concatenate</option>
                <option value="upper">Uppercase</option>
                <option value="lower">Lowercase</option>
                <option value="length">Length</option>
                <option value="split">Split</option>
                <option value="join">Join</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Input Variable
              </label>
              <input
                type="text"
                value={config.inputVariable || ''}
                onChange={(e) => handleConfigChange('inputVariable', e.target.value)}
                placeholder="variableName"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Output Variable
              </label>
              <input
                type="text"
                value={config.outputVariable || ''}
                onChange={(e) => handleConfigChange('outputVariable', e.target.value)}
                placeholder="result"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {(config.functionName === 'split' || config.functionName === 'join') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Separator
                </label>
                <input
                  type="text"
                  value={config.separator || ''}
                  onChange={(e) => handleConfigChange('separator', e.target.value)}
                  placeholder=","
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
          </>
        );

      case 'code':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Code Expression
              </label>
              <textarea
                value={config.code || ''}
                onChange={(e) => handleConfigChange('code', e.target.value)}
                placeholder="result = {{input}} * 2 + 1"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Output Variable
              </label>
              <input
                type="text"
                value={config.outputVariable || ''}
                onChange={(e) => handleConfigChange('outputVariable', e.target.value)}
                placeholder="result"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="text-xs text-gray-500">
              Safe expressions only. Available: +, -, *, /, %, ==, !=, &lt;, &gt;, and, or, not
            </div>
          </>
        );

      case 'dataTransform':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transform Expression
              </label>
              <textarea
                value={config.transform || ''}
                onChange={(e) => handleConfigChange('transform', e.target.value)}
                placeholder="{{input}}.upper() or {{data}}.split(',') or custom transformation"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-20 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Output Variable
              </label>
              <input
                type="text"
                value={config.outputVariable || ''}
                onChange={(e) => handleConfigChange('outputVariable', e.target.value)}
                placeholder="transformed"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </>
        );

      case 'dataOutput':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Output Format
              </label>
              <select
                value={config.format || 'json'}
                onChange={(e) => handleConfigChange('format', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="json">JSON</option>
                <option value="text">Text</option>
                <option value="csv">CSV</option>
                <option value="xml">XML</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data to Output
              </label>
              <textarea
                value={config.data || ''}
                onChange={(e) => handleConfigChange('data', e.target.value)}
                placeholder="{{result}} or specific data to output"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-20 resize-none"
              />
            </div>
          </>
        );

      case 'textAnalysis':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Analysis Type
              </label>
              <select
                value={config.analysisType || 'sentiment'}
                onChange={(e) => handleConfigChange('analysisType', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="sentiment">Sentiment Analysis</option>
                <option value="entities">Entity Extraction</option>
                <option value="keywords">Keyword Extraction</option>
                <option value="summary">Text Summary</option>
                <option value="language">Language Detection</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Input Text Variable
              </label>
              <input
                type="text"
                value={config.inputVariable || ''}
                onChange={(e) => handleConfigChange('inputVariable', e.target.value)}
                placeholder="text"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </>
        );

      case 'intent':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Intent Classification Method
              </label>
              <select
                value={config.method || 'keywords'}
                onChange={(e) => handleConfigChange('method', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="keywords">Keyword Matching</option>
                <option value="llm">LLM Classification</option>
                <option value="regex">Regex Patterns</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Intents (intent:keywords, one per line)
              </label>
              <textarea
                value={config.intents?.map(i => `${i.name}:${i.keywords?.join(',') || i.pattern || ''}`).join('\n') || ''}
                onChange={(e) => {
                  const lines = e.target.value.split('\n').filter(line => line.trim());
                  const intents = lines.map(line => {
                    const [name, keywordsStr] = line.split(':');
                    return {
                      name: name?.trim(),
                      keywords: keywordsStr?.split(',').map(k => k.trim()).filter(Boolean) || [],
                      pattern: keywordsStr?.trim()
                    };
                  });
                  handleConfigChange('intents', intents);
                }}
                placeholder="greeting:hello,hi,hey&#10;question:what,how,when,where&#10;goodbye:bye,farewell,see you"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Input Variable
              </label>
              <input
                type="text"
                value={config.inputVariable || ''}
                onChange={(e) => handleConfigChange('inputVariable', e.target.value)}
                placeholder="userMessage"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </>
        );

      case 'integration':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Integration Type
              </label>
              <select
                value={config.integrationType || 'webhook'}
                onChange={(e) => handleConfigChange('integrationType', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="webhook">Generic Webhook</option>
                <option value="slack">Slack</option>
                <option value="discord">Discord</option>
                <option value="email">Email</option>
                <option value="zapier">Zapier</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Webhook URL / Endpoint
              </label>
              <input
                type="text"
                value={config.url || ''}
                onChange={(e) => handleConfigChange('url', e.target.value)}
                placeholder="https://hooks.slack.com/services/..."
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message/Payload
              </label>
              <textarea
                value={config.message || ''}
                onChange={(e) => handleConfigChange('message', e.target.value)}
                placeholder="Message content with {{variables}}"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-20 resize-none"
              />
            </div>
          </>
        );

      case 'loop':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Loop Type
              </label>
              <select
                value={config.loopType || 'forEach'}
                onChange={(e) => handleConfigChange('loopType', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="forEach">For Each Item</option>
                <option value="while">While Condition</option>
                <option value="count">Count (N times)</option>
              </select>
            </div>
            {config.loopType === 'forEach' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Array Variable
                </label>
                <input
                  type="text"
                  value={config.arrayVariable || ''}
                  onChange={(e) => handleConfigChange('arrayVariable', e.target.value)}
                  placeholder="items"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
            {config.loopType === 'while' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  While Condition
                </label>
                <input
                  type="text"
                  value={config.condition || ''}
                  onChange={(e) => handleConfigChange('condition', e.target.value)}
                  placeholder="{{counter}} < 10"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
            {config.loopType === 'count' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Count
                </label>
                <input
                  type="number"
                  value={config.count || ''}
                  onChange={(e) => handleConfigChange('count', parseInt(e.target.value) || 0)}
                  placeholder="5"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Item Variable Name
              </label>
              <input
                type="text"
                value={config.itemVariable || ''}
                onChange={(e) => handleConfigChange('itemVariable', e.target.value)}
                placeholder="item"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </>
        );

      case 'trigger':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trigger Type
              </label>
              <select
                value={config.triggerType || 'manual'}
                onChange={(e) => handleConfigChange('triggerType', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="manual">Manual Start</option>
                <option value="webhook">Webhook</option>
                <option value="schedule">Scheduled</option>
                <option value="event">Event</option>
              </select>
            </div>
            {config.triggerType === 'webhook' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Webhook Path
                </label>
                <input
                  type="text"
                  value={config.webhookPath || ''}
                  onChange={(e) => handleConfigChange('webhookPath', e.target.value)}
                  placeholder="/webhook/my-flow"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
            {config.triggerType === 'schedule' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cron Expression
                </label>
                <input
                  type="text"
                  value={config.schedule || ''}
                  onChange={(e) => handleConfigChange('schedule', e.target.value)}
                  placeholder="0 9 * * 1-5"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
          </>
        );

      case 'end':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Message
              </label>
              <textarea
                value={config.message || ''}
                onChange={(e) => handleConfigChange('message', e.target.value)}
                placeholder="Flow completed successfully. Final result: {{result}}"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-20 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Return Data
              </label>
              <textarea
                value={config.returnData || ''}
                onChange={(e) => handleConfigChange('returnData', e.target.value)}
                placeholder="{{finalResult}} or specific data to return"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-16 resize-none"
              />
            </div>
          </>
        );

      case 'composio':
        return <ComposioConfig config={config} onChange={handleConfigChange} />;

      default:
        return (
          <div className="text-gray-500 text-center py-4">
            No configuration options available for this node type.
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-md shadow-xl w-full max-w-sm mx-3">
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-800">
            Configure Node
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-3 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Node Label
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full p-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="Enter node label..."
            />
          </div>

          {renderConfigFields()}
        </div>

        <div className="flex items-center justify-end gap-2 p-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-gray-600 hover:text-gray-800 transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
          >
            <Save size={13} />
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default NodeConfigModal;
